import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  Plus,
  Building2,
  CreditCard,
  Smartphone,
  Trash2,
  Check,
  X,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import apiClient from '../../api/client';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const BankChannelsPage = () => {
  const navigate = useNavigate();
  const { module = 'retail' } = useFiscalPeriod();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [formData, setFormData] = useState({
    channel_name: '',
    channel_type: 'bank_transfer',
    bank_name: '',
    account_number: '',
    account_name: '',
    base_charge: 0,
    charge_percentage: 0,
    charge_variance: 0,
    charge_cap: '',
    settlement_days: 0,
    is_default: false,
    notes: ''
  });

  useEffect(() => {
    fetchChannels();
  }, [module]);

  const fetchChannels = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/bank-channels?include_inactive=true&module=${module}`);
      setChannels(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch bank channels');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        charge_cap: formData.charge_cap ? parseFloat(formData.charge_cap) : null,
        module: module
      };

      if (editingChannel) {
        await apiClient.put(`/bank-channels/${editingChannel.id}`, payload);
        toast.success('Bank channel updated successfully');
      } else {
        await apiClient.post('/bank-channels', payload);
        toast.success('Bank channel created successfully');
      }

      setShowModal(false);
      setEditingChannel(null);
      resetForm();
      fetchChannels();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save bank channel');
    }
  };

  const handleEdit = (channel) => {
    setEditingChannel(channel);
    setFormData({
      channel_name: channel.channel_name,
      channel_type: channel.channel_type,
      bank_name: channel.bank_name || '',
      account_number: channel.account_number || '',
      account_name: channel.account_name || '',
      base_charge: channel.base_charge || 0,
      charge_percentage: channel.charge_percentage || 0,
      charge_variance: channel.charge_variance || 0,
      charge_cap: channel.charge_cap || '',
      settlement_days: channel.settlement_days || 0,
      is_default: channel.is_default || false,
      notes: channel.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this bank channel?')) return;

    try {
      await apiClient.delete(`/bank-channels/${id}`);
      toast.success('Bank channel deleted');
      fetchChannels();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    }
  };

  const toggleActive = async (channel) => {
    try {
      await apiClient.put(`/bank-channels/${channel.id}`, {
        is_active: !channel.is_active
      });
      toast.success(`Channel ${channel.is_active ? 'deactivated' : 'activated'}`);
      fetchChannels();
    } catch (error) {
      toast.error('Failed to update channel');
    }
  };

  const resetForm = () => {
    setFormData({
      channel_name: '',
      channel_type: 'bank_transfer',
      bank_name: '',
      account_number: '',
      account_name: '',
      base_charge: 0,
      charge_percentage: 0,
      charge_variance: 0,
      charge_cap: '',
      settlement_days: 0,
      is_default: false,
      notes: ''
    });
  };

  const getChannelIcon = (type) => {
    switch (type) {
      case 'bank_transfer':
        return <Building2 className="h-5 w-5" />;
      case 'pos':
        return <CreditCard className="h-5 w-5" />;
      case 'card':
        return <CreditCard className="h-5 w-5" />;
      default:
        return <Smartphone className="h-5 w-5" />;
    }
  };

  const formatCharge = (channel) => {
    const parts = [];
    if (parseFloat(channel.base_charge) > 0) {
      parts.push(`₦${channel.base_charge} fixed`);
    }
    if (parseFloat(channel.charge_percentage) > 0) {
      parts.push(`${channel.charge_percentage}%`);
    }
    if (parts.length === 0) return 'No charges';
    return parts.join(' + ');
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bank Channels</h1>
            <p className="text-sm text-gray-500">Manage bank accounts and POS terminals</p>
          </div>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingChannel(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg"
        >
          <Plus className="h-4 w-4" />
          Add Channel
        </button>
      </div>

      {/* Channels List */}
      {loading ? (
        <LoadingSpinner message="Loading bank channels..." className="py-12" />
      ) : channels.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No bank channels</h3>
          <p className="text-gray-500 mb-4">Add your first bank account or POS terminal</p>
          <button
            onClick={() => setShowModal(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg"
          >
            Add Channel
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {channels.map((channel) => (
            <div
              key={channel.id}
              onClick={() => handleEdit(channel)}
              className={`bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${!channel.is_active ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div
                    className={`p-2 rounded-lg ${
                      channel.channel_type === 'bank_transfer'
                        ? 'bg-blue-100 text-blue-600'
                        : channel.channel_type === 'pos'
                          ? 'bg-green-100 text-green-600'
                          : 'bg-purple-100 text-purple-600'
                    }`}
                  >
                    {getChannelIcon(channel.channel_type)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{channel.channel_name}</h3>
                      {channel.is_default && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                          Default
                        </span>
                      )}
                      {!channel.is_active && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {channel.channel_type === 'bank_transfer'
                        ? 'Bank Transfer'
                        : channel.channel_type === 'pos'
                          ? 'POS Terminal'
                          : 'Card Payment'}
                      {channel.bank_name && ` • ${channel.bank_name}`}
                    </p>
                    {channel.account_number && (
                      <p className="text-sm text-gray-400">{channel.account_number}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => toggleActive(channel)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                    title={channel.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {channel.is_active ? (
                      <ToggleRight className="h-5 w-5 text-green-600" />
                    ) : (
                      <ToggleLeft className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {!channel.is_default && (
                    <button
                      onClick={() => handleDelete(channel.id)}
                      className="p-2 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  )}
                </div>
              </div>

              {/* Charges Info */}
              <div className="mt-3 pt-3 border-t flex flex-wrap gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Charges:</span>
                  <span className="ml-1 font-medium">{formatCharge(channel)}</span>
                </div>
                {parseFloat(channel.charge_variance) > 0 && (
                  <div>
                    <span className="text-gray-500">Variance:</span>
                    <span className="ml-1">±₦{channel.charge_variance}</span>
                  </div>
                )}
                {channel.settlement_days > 0 && (
                  <div>
                    <span className="text-gray-500">Settlement:</span>
                    <span className="ml-1">{channel.settlement_days} days</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingChannel ? 'Edit Bank Channel' : 'Add Bank Channel'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingChannel(null);
                }}
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel Name *
                </label>
                <input
                  type="text"
                  value={formData.channel_name}
                  onChange={(e) => setFormData({ ...formData, channel_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g., GTBank Main Account"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel Type *
                </label>
                <select
                  value={formData.channel_type}
                  onChange={(e) => setFormData({ ...formData, channel_type: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="pos">POS Terminal</option>
                  <option value="card">Card Payment</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="GTBank"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Number
                  </label>
                  <input
                    type="text"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="0123456789"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
                <input
                  type="text"
                  value={formData.account_name}
                  onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Business Name Ltd"
                />
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">Charge Configuration</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Base Charge (₦)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.base_charge}
                      onChange={(e) => setFormData({ ...formData, base_charge: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Charge %</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.charge_percentage}
                      onChange={(e) =>
                        setFormData({ ...formData, charge_percentage: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="e.g., 1.5 for 1.5%"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Variance ±₦
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.charge_variance}
                      onChange={(e) =>
                        setFormData({ ...formData, charge_variance: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Charge Cap (₦)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.charge_cap}
                      onChange={(e) => setFormData({ ...formData, charge_cap: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="Leave empty for no cap"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Settlement Days
                </label>
                <input
                  type="number"
                  value={formData.settlement_days}
                  onChange={(e) => setFormData({ ...formData, settlement_days: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="0 for immediate"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="is_default" className="text-sm text-gray-700">
                  Set as default channel
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingChannel(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <Check className="h-4 w-4" />
                  {editingChannel ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankChannelsPage;
